/**
 * Monitoring API - observability stack URLs and monitoring-related endpoints.
 */

import { request } from './core'

export interface ObservabilityToolInfo {
  enabled: boolean
  url: string | null
}

export interface ObservabilityUrlsResponse {
  prometheus: ObservabilityToolInfo
  grafana: ObservabilityToolInfo
  kibana: ObservabilityToolInfo
  elasticsearch: ObservabilityToolInfo
  jaeger: ObservabilityToolInfo
}

export interface LlmConsumptionUser {
  id: string | null
  email: string | null
}

export interface LlmConsumptionItem {
  id: string
  occurred_at: string
  occurred_at_us: number
  user: LlmConsumptionUser
  consumer_type: string | null
  consumer_id: string | null
  source_service: string
  model_id: string
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  total_tokens: number
  latency_ms: number | null
  status: string | null
  estimated_cost_usd: number
  estimated_cost_display: string
  workflow_id: string | null
  workflow_name: string | null
  execution_id: string | null
  agent_id: string | null
  agent_name: string | null
  extra: Record<string, unknown>
}

export interface LlmConsumptionPage {
  items: LlmConsumptionItem[]
  next_cursor: string | null
  limit: number
  total?: number
  filters_applied: Record<string, unknown>
}

export interface LlmConsumptionFacets {
  consumer_types: string[]
  source_services: string[]
  model_ids: string[]
}

export interface LlmConsumptionUserSummary {
  user_id: string | null
  user_email: string | null
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  total_tokens: number
  llm_calls: number
  estimated_cost_usd: number
  estimated_cost_display: string
}

export interface LlmConsumptionUserSummaryPage {
  items: LlmConsumptionUserSummary[]
  total: number
  page: number
  page_size: number
  filters_applied: Record<string, unknown>
}

export interface LlmConsumptionUserSummaryFilters {
  tenant_id?: string
  consumer_type?: string[]
  source_service?: string[]
  model_id?: string
  period?: string
  start_date?: string
  end_date?: string
  page?: number
  page_size?: number
  order_by?: string
  order_desc?: boolean
  environment?: string
}

export interface LlmConsumptionFilters {
  tenant_id?: string
  consumer_type?: string[]
  source_service?: string[]
  model_id?: string
  user_id?: string
  period?: string
  start_date?: string
  end_date?: string
  cursor?: string
  limit?: number
  page?: number
  page_size?: number
  search?: string
  agent_id?: string
  workflow_id?: string
  order_by?: string
  order_desc?: boolean
  environment?: string
}

function appendLlmConsumptionFilters(sp: URLSearchParams, filters: LlmConsumptionFilters): void {
  if (filters.tenant_id) sp.set('tenant_id', filters.tenant_id)
  if (filters.consumer_type?.length) sp.set('consumer_type', filters.consumer_type.join(','))
  if (filters.source_service?.length) sp.set('source_service', filters.source_service.join(','))
  if (filters.model_id) sp.set('model_id', filters.model_id)
  if (filters.user_id) sp.set('user_id', filters.user_id)
  if (filters.period) sp.set('period', filters.period)
  if (filters.start_date) sp.set('start_date', filters.start_date)
  if (filters.end_date) sp.set('end_date', filters.end_date)
  if (filters.cursor) sp.set('cursor', filters.cursor)
  if (filters.limit) sp.set('limit', String(filters.limit))
  if (filters.page) sp.set('page', String(filters.page))
  if (filters.page_size) sp.set('page_size', String(filters.page_size))
  if (filters.search) sp.set('search', filters.search)
  if (filters.agent_id) sp.set('agent_id', filters.agent_id)
  if (filters.workflow_id) sp.set('workflow_id', filters.workflow_id)
  if (filters.order_by) sp.set('order_by', filters.order_by)
  if (filters.order_desc !== undefined) sp.set('order_desc', String(filters.order_desc))
  if (filters.environment) sp.set('environment', filters.environment)
}

function _toQuery(filters: LlmConsumptionFilters): string {
  const sp = new URLSearchParams()
  appendLlmConsumptionFilters(sp, filters)
  const qs = sp.toString()
  return qs ? `?${qs}` : ''
}

export const monitoringApi = {
  getObservabilityUrls: (): Promise<ObservabilityUrlsResponse> =>
    request<ObservabilityUrlsResponse>('/v1/observability/urls'),

  /** Cursor-style feed of every LLM call across the platform. */
  listLlmConsumption: (filters: LlmConsumptionFilters = {}): Promise<LlmConsumptionPage> =>
    request<LlmConsumptionPage>(`/v1/monitoring/llm-consumption${_toQuery(filters)}`),

  /** Distinct values used to populate the filter dropdowns. */
  getLlmConsumptionFacets: (tenantId?: string): Promise<LlmConsumptionFacets> => {
    const querySuffix = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ''
    return request<LlmConsumptionFacets>(
      `/v1/monitoring/llm-consumption/facets${querySuffix}`
    )
  },

  /** Database-aggregated, paginated per-user token/cost rollup. */
  listLlmConsumptionUserSummaries: (
    filters: LlmConsumptionUserSummaryFilters = {}
  ): Promise<LlmConsumptionUserSummaryPage> =>
    request<LlmConsumptionUserSummaryPage>(
      `/v1/monitoring/llm-consumption/user-summaries${_toQuery(filters as LlmConsumptionFilters)}`
    ),
}
